/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx, css } from '@emotion/react';
import * as Yup from "yup";
import { 
    Pivot, PivotItem,
} from '@fluentui/react';
import BasicInfoFields from "../Components/BasicInfoFields";
import PlaylistContent from "../Components/PlaylistContent";
import CobrandsOffSettings from '../Components/CobrandsOffSettings';
import NavigationalButtons from '../../../../Util/NavigationalButtons';
import PrimaryButton from '../../../../Forms/PrimaryButton';
import ImageUploadStep from '../../../../Forms/Steps/ImageUploadStep';

const route = 'resources/content/admin/series';

const rootCss = css`
    max-width: 100%;

    .form-container {
        width: 100%;
        display: flex;
        flex-direction: column;
        min-height: 80vh;
        
        .step-box {
            flex-grow: 1;
            min-height: 300px;
        }

        .nav-btn-box {
            margin-top: auto; 
            width: 30%;
            margin-left: auto;
            margin-right: auto;
        }

        .ms-Button {
            align-self: center;
        }
        .ms-Button--primary:not(.is-disabled) {
            height: 36px;
            .ms-Button-flexContainer {
                span {
                    color: #fff;
                }
            }
        }
        .save-btn-container {
            display: flex;
            margin-top: .25rem !important;
        }
    }

    .max-800 {
        max-width: 800px;
    }
    
    .preview-image {
        margin-bottom: 20px;
    }

    .preview-upload-help {
        margin: 10px 0 30px 0;
        font-size: 14px;
        color: var(--Z8);
    }
`;

const validationSchema = Yup.object({
    conPlaylistID: Yup.number().required("Playlist ID is required"),
    name: Yup.string()
        .required("Name is required")
        .max(128, "Name cannot exceed 128 characters"),
    slug: Yup.string()
        .required("Slug is required")
        .matches(/^[a-z0-9-]+$/, "Slug must be lowercase and contain only letters, numbers, and hyphens")
        .max(255, "Slug cannot exceed 255 characters"),
    icon: Yup.string()
        .url("Icon must be a valid URL")
        .nullable(),
    seriesContent: Yup.array()
        .of(
            Yup.object({
                playlistId: Yup.number().required("Playlist ID is required"),
                seriesTitle: Yup.string().required("Series Title is required"),
                contentTitle: Yup.string().required("Content Title is required"),
                contentId: Yup.number().required("Content ID is required"),
                adminPath: Yup.string().required("Admin Path is required"),
                orderNumber: Yup.number().nullable(),
            })
        )
        .min(1, "At least one content must be added"),
    offCobrands: Yup.array()
        .of(Yup.number().required("Cobrand ID is required"))
        .nullable()
});

function mapInitialValues(data) {
    if (data.seriesData) {
        const { seriesData } = data;
        return {
            conPlaylistID: seriesData.conPlaylistID || 0,
            name: seriesData.name || '',
            slug: seriesData.slug || '',
            icon: seriesData.icon || '', 
            seriesContent: seriesData.seriesContent || [],
            offCobrands: seriesData.offCobrands || [],
        };
    } else {
        return {
            conPlaylistID: 0,
            name: '',
            slug: '',
            icon: '',
            seriesContent: [],
            offCobrands: [],
        };
    }
}

const setErrorMsg = (show, errors) => (
    show && Object.keys(errors).length > 0
        ? `Errors in: ${Object.keys(errors).join(', ')}. Check all required * fields.`
        : "Errors in form, check all required fields and try again."
);

const getSeriesType = (model) => {
    const typeCounts = model.reduce((acc, item) => {
        acc[item.contentType] = (acc[item.contentType] || 0) + 1;
        return acc;
    }, {});
    
    const sortedTypes = Object.entries(typeCounts).sort((a, b) => b[1] - a[1]);
    const mostCommonType = sortedTypes[0]?.[0] ?? "";

    return { mostCommonType }
};

const sortEligibleData = (eligibleData, model) => {
    const { mostCommonType } = getSeriesType(model);
    const sortedEligibleData = eligibleData.filter(content =>
        (mostCommonType === "Article" && content.contentType === "Article") ||
        (mostCommonType === "Video" && content.contentType === "Video")
    );

    return sortedEligibleData;
};

const getImgOptions = (values) => ({
    name: 'icon',
    label: 'Icon',
    helpMsg: "* Upload a new icon or select one from our Series library.",
    previewUrl: values.icon,
    width: 168,
    height: 168,
    gallery: {
        name: "icon",
        blobUrl: "https://igradcdnproduction.blob.core.windows.net",
        containerName: "media",
        folderName: "IMAGE/icons/Prioritized-Financial-Plan/",
        imageUrl: "https://media-cdn.igrad.com/",
        heading: "Select an icon or upload a new one",
        btnText: "Select Icon"
    },
});

const basicInfoFields = [
    { name: "name", label: "Name", required: true },
    { name: "slug", label: "Slug", required: true }
];

const unsavedMsg = 'You have unsaved changes, are you sure you want to leave?';

const StepBox = ({ selectedKey, handleLinkClick, eligibleData, imageOptions }) => {
    return (
        <div className="step-box">
            <Pivot selectedKey={String(selectedKey)} onLinkClick={handleLinkClick}>
                <PivotItem headerText="Basic Info" itemKey="0">
                    <div className="max-800">
                        <BasicInfoFields />
                    </div>
                </PivotItem>
                <PivotItem headerText="Icon" itemKey="1">
                    <div className="max-800">
                        <ImageUploadStep options={imageOptions} />
                    </div>
                </PivotItem>
                <PivotItem headerText="Playlist Content" itemKey="2">
                    <PlaylistContent allContent={eligibleData} />
                </PivotItem>
                <PivotItem headerText="Cobrand Off Settings" itemKey="3">
                    <div className="max-800">
                        <CobrandsOffSettings />
                    </div>
                </PivotItem>
            </Pivot>
        </div>
    );
};

const FIRST_STEP = "0";
const LAST_STEP = "3";

const NavigationalButtonsBox = ({ selectedKey, nextOnClick, prevOnClick }) => {
    return (
        <div className="nav-btn-box">
            <NavigationalButtons
                onNextHandler={selectedKey !== LAST_STEP ? nextOnClick : false}
                onPrevHandler={selectedKey !== FIRST_STEP ? prevOnClick : false}
            />
        </div>
    );
};

const SaveButton = ({ handleSave, dirty, submitting }) => {
    return (
        <div className="save-btn-container">
            <PrimaryButton
                onClick={handleSave}
                text="Save"
                disabled={!dirty || submitting}
            />
        </div>
    );
};


export {
    route,
    rootCss,
    validationSchema,
    mapInitialValues,
    setErrorMsg,
    sortEligibleData,
    getImgOptions, 
    basicInfoFields,
    unsavedMsg,
    StepBox,
    NavigationalButtonsBox,
    SaveButton,
};
